import axios from 'axios';
import { PaymentGateway } from '../applications/types/applications.types';
import { CURSEDUCA_PLANS_REFERENCES } from '../../pages/Checkout/components/Form';
import { CheckoutInputError } from '../../utils/checkout-input-error';

export type TokenizeCreditCardDto = {
	hasAntifraud: boolean;
	type: 'card';
	// Gateway resolvido pela plataforma (via /applications/params). Decide onde
	// o cartão é tokenizado. Ausente => heurística legada (Vindi por path / Pagar.me).
	gateway?: PaymentGateway;
	card: {
		number: string;
		holder_name: string;
		exp_month: number;
		exp_year: number;
		cvv: string;
	};
};

export type TokenizeCreditCardResponse = {
	id: string;
	type: 'card';
	created_at: string;
	expires_at: string;
	card: {
		first_six_digits: string;
		last_four_digits: string;
		holder_name: string;
		exp_month: number;
		exp_year: number;
		brand: string;
	};
};

export const tokenize = async (data: TokenizeCreditCardDto) => {
	const today = new Date();
	const expiryDate = new Date();
	expiryDate.setFullYear(
		2000 + Number(data.card.exp_year),
		Number(data.card.exp_month),
		1
	);

	if (expiryDate < today) {
		throw new CheckoutInputError('Cartão informado está expirado.');
	}

	// 1) Gateway explícito da plataforma (fonte da verdade = backend). O token
	//    precisa ser emitido no mesmo gateway que o backend vai cobrar.
	if (data.gateway === 'MALGA') {
		return malgaTokenize(data);
	}
	if (data.gateway === 'VINDI') {
		return vindiTokenize(data);
	}
	if (data.gateway === 'PAGARME') {
		return pagarmeTokenize(data);
	}

	// 2) Retro-compat: sem gateway explícito, mantém a heurística legada
	//    (Vindi por path do plano; caso contrário Pagar.me).
	const isVindi = !!Object.keys(CURSEDUCA_PLANS_REFERENCES).find((r) => {
		return location.pathname.includes(r);
	});

	if (isVindi) {
		return vindiTokenize(data);
	}

	return pagarmeTokenize(data);
};

const pagarmeTokenize = async ({
	hasAntifraud,
	...data
}: TokenizeCreditCardDto) => {
	const publicKey = hasAntifraud
		? process.env.REACT_APP_PAGARME_PUBLIC_KEY
		: process.env.REACT_APP_PAGARME_PUBLIC_KEY_WITHOUT_ANTIFRAUD;

	const response = await axios.post<TokenizeCreditCardResponse>(
		`https://api.pagar.me/core/v5/tokens?appId=${publicKey}`,
		data
	);

	return response.data;
};

const vindiTokenize = async ({ card }: TokenizeCreditCardDto) => {
	const r = await fetch(
		'https://app.vindi.com.br/api/v1/public/payment_profiles',
		{
			method: 'POST',
			body: JSON.stringify({
				holder_name: card.holder_name,
				card_expiration: `${card.exp_month}/${card.exp_year}`,
				allow_as_fallback: true,
				card_number: card.number,
				card_cvv: card.cvv,
				payment_method_code: 'credit_card'
			}),
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Basic ${btoa(
					process.env.REACT_APP_VINDI_API_KEY as string
				)}`
			}
		}
	);

	const j = await r.json().catch(() => null);

	// A Vindi recusa dado de cartão com 422 `{ errors: [{ message }] }`. Antes isto virava
	// TypeError em `j.payment_profile` e ia ao #alertas-checkout como erro nosso. 401/403 (chave
	// do build) não é do comprador: segue abaixo e vai ao Sentry.
	if (r.status === 400 || r.status === 422) {
		const reason = j?.errors?.[0]?.message;
		throw new CheckoutInputError(
			typeof reason === 'string' && reason
				? `Confira os dados do cartão: ${reason}`
				: 'Confira os dados do cartão e tente novamente.'
		);
	}
	const gatewayToken = j?.payment_profile?.gateway_token;
	if (!r.ok || typeof gatewayToken !== 'string' || !gatewayToken) {
		// Falha da Vindi (5xx), chave recusada ou resposta fora do contrato: vai ao Sentry. Só o
		// 5xx leva o status — com `status` < 500 o `reportCheckoutError` leria "não é do servidor".
		// A mensagem não diz "HTTP 401"/"failed: 401": o `getHttpStatus` leria o 4xx do texto.
		const error = new Error(
			`Vindi tokenization returned ${r.status} without a token`
		);
		throw r.status >= 500
			? Object.assign(error, { status: r.status })
			: error;
	}

	return {
		card: {
			first_six_digits: card.number.slice(0, 6),
			last_four_digits: card.number.slice(-4),
			holder_name: card.holder_name,
			exp_month: card.exp_month,
			exp_year: card.exp_year,
			brand: ''
		},
		id: gatewayToken
	};
};

// Tokenização client-side da Malga: POST /v1/tokens → { tokenId }. O cartão vai do
// browser direto para a Malga; para o nosso backend só segue o token.
//
// ⚠️ SEGURANÇA — toda REACT_APP_* é embutida no bundle público:
//  - REACT_APP_MALGA_CLIENT_ID: identificador da conta. Não é segredo (a Malga o
//    quer visível no client-side); sozinho não autentica nada.
//  - REACT_APP_MALGA_API_KEY: TEM que ser a chave PÚBLICA de escopo ["tokens"],
//    gerada por `POST /v1/auth` — vai no mesmo header X-Api-Key. NUNCA a chave
//    secreta do backend (`MALGA_API_KEY` do curseduca-pay), que cria cobrança,
//    seller e estorno. Com a pública, /charges, /sellers e /webhooks respondem 403.
//    Doc: docs.malga.io/api-reference/client-token
const malgaTokenize = async ({
	card
}: TokenizeCreditCardDto): Promise<TokenizeCreditCardResponse> => {
	const baseUrl =
		process.env.REACT_APP_MALGA_BASE_URL || 'https://api.malga.io/v1';
	const clientId = process.env.REACT_APP_MALGA_CLIENT_ID;
	const apiKey = process.env.REACT_APP_MALGA_API_KEY;

	// Sem isto os headers iam como `undefined` e a Malga devolvia um 401 opaco —
	// o comprador via "erro ao processar cartão" e o motivo real era build sem env.
	if (!clientId || !apiKey) {
		throw new Error(
			'Checkout sem credenciais da Malga (REACT_APP_MALGA_CLIENT_ID / REACT_APP_MALGA_API_KEY ausentes no build).'
		);
	}

	// Malga exige validade no formato MM/YYYY. exp_year pode vir com 2 dígitos.
	const expMonth = String(card.exp_month).padStart(2, '0');
	const expYear =
		String(card.exp_year).length === 2
			? `20${card.exp_year}`
			: String(card.exp_year);

	const { data } = await axios.post<{ tokenId: string }>(
		`${baseUrl}/tokens`,
		{
			cardNumber: card.number,
			cardHolderName: card.holder_name,
			cardCvv: card.cvv,
			cardExpirationDate: `${expMonth}/${expYear}`
		},
		{
			headers: {
				'Content-Type': 'application/json',
				'X-Client-Id': clientId,
				'X-Api-Key': apiKey
			}
		}
	);

	return {
		id: data.tokenId,
		type: 'card',
		created_at: '',
		expires_at: '',
		card: {
			first_six_digits: card.number.slice(0, 6),
			last_four_digits: card.number.slice(-4),
			holder_name: card.holder_name,
			exp_month: card.exp_month,
			exp_year: card.exp_year,
			brand: ''
		}
	};
};
